import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { useLoggedInUser } from 'src/utils/hooks/useLoggedInUser';
import { useTaskDelegationIntl } from '../translations/useTaskDelegationIntl';
import { TaskDelegationVariables } from './TaskDelegation.types';
import { GET_TASK_DELEGATIONS, GET_TASK_DELEGATIONS_COUNT } from './TaskDelegation.queries';

interface Props {
  variables?: Partial<TaskDelegationVariables>;
  myAccount?: boolean;
  count?: boolean;
}

export const useTaskDelegationQuery = ({ variables, myAccount, count }: Props) => {
  const intl = useTaskDelegationIntl();
  const loggedInUser = useLoggedInUser();

  const { showError } = useSnackbar();

  const { data, loading, refetch, error } = useQuery<TaskDelegationQuery, Partial<TaskDelegationVariables>>(
    count ? GET_TASK_DELEGATIONS_COUNT : GET_TASK_DELEGATIONS,
    {
      variables: {
        ...variables,
        query: {
          ...variables?.query,
          ...(myAccount && {
            originalAssigneeUid: loggedInUser?.person?.personUid
          })
        }
      },
      skip: !variables?.page,
      onError: (queryError) =>
        showError(queryError?.message ? `${intl.somethingWentWrong}: ${queryError?.message}` : intl.somethingWentWrong)
    }
  );

  const taskDelegations = useMemo(
    () => data?.taskDelegations?.items?.map((taskDelegation) => taskDelegation) || [],
    [data?.taskDelegations?.items]
  );
  const totalCount = useMemo(() => data?.taskDelegations?.totalCount ?? 0, [data?.taskDelegations?.totalCount]);

  return { taskDelegations, loading, refetch, error, totalCount };
};

type TaskDelegationQuery = { taskDelegations: { items: []; totalCount: number } };
